<template>
  <div class="themed-bg min-h-screen p-4 pb-20">
    <!-- Header -->
    <div class="flex justify-between items-center mb-4">
      <div>
        <h2 class="text-2xl font-bold">Research & Insights</h2>
        <p class="themed-text-secondary">Data-driven farming intelligence</p>
      </div>
      <ThemeToggle />
    </div>

    <!-- Region Selector -->
    <div class="glass-card p-4 mb-4">
      <label class="block text-sm font-medium mb-2">Select Region</label>
      <select v-model="selectedRegion" @change="loadData" class="input">
        <option value="central">ภาคกลาง (Central)</option>
        <option value="north">ภาคเหนือ (North)</option>
        <option value="northeast">ภาคตะวันออกเฉียงเหนือ (Northeast)</option>
        <option value="south">ภาคใต้ (South)</option>
      </select>
    </div>

    <!-- Weather Alerts -->
    <div class="glass-card p-4 mb-4">
      <h3 class="font-bold mb-3 flex items-center gap-2">
        <span class="pulse-dot"></span>
        Weather Alerts
      </h3>
      <div v-if="weatherAlerts.length === 0" class="text-center py-4 themed-text-secondary">
        No active weather alerts
      </div>
      <div v-else class="space-y-3">
        <div v-for="alert in weatherAlerts" :key="alert.id" 
             class="p-3 rounded-lg border-l-4"
             :class="alertSeverityClass(alert.severity)">
          <div class="flex justify-between items-start mb-2">
            <h4 class="font-semibold">{{ alert.title }}</h4>
            <span :class="alertTypeClass(alert.type)" class="text-xs px-2 py-1 rounded">
              {{ alert.type }}
            </span>
          </div>
          <p class="text-sm themed-text-secondary mb-2">{{ alert.description }}</p>
          <div class="text-xs themed-text-secondary">
            <p>📍 {{ alert.regions.join(', ') }}</p>
            <p>🕐 {{ formatDateTime(alert.startTime) }} - {{ formatDateTime(alert.endTime) }}</p>
          </div>
          <div v-if="alert.recommendations" class="mt-2">
            <p class="text-xs font-semibold mb-1">Recommendations:</p>
            <ul class="text-xs themed-text-secondary list-disc list-inside">
              <li v-for="rec in alert.recommendations" :key="rec">{{ rec }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Market Prices -->
    <div class="glass-card p-4 mb-4">
      <h3 class="font-bold mb-3">Market Prices</h3>
      <div v-if="Object.keys(marketPrices).length === 0" class="skeleton h-20 rounded"></div>
      <div v-else class="space-y-3">
        <div v-for="(price, crop) in marketPrices" :key="crop" 
             class="flex justify-between items-center p-3 rounded-lg bg-white/5 dark:bg-black/5">
          <div>
            <h4 class="font-semibold capitalize">{{ crop }}</h4>
            <p class="text-sm themed-text-secondary">
              {{ price.current }} {{ price.unit }}
            </p>
          </div>
          <div class="text-right">
            <div :class="price.change > 0 ? 'text-green-600' : 'text-red-600'" class="font-semibold">
              {{ price.change > 0 ? '↑' : '↓' }} {{ Math.abs(price.change) }}%
            </div>
            <p class="text-xs themed-text-secondary">{{ price.trend }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Crop Information -->
    <div class="glass-card p-4 mb-4">
      <h3 class="font-bold mb-3">Crop Information</h3>
      <div class="grid grid-cols-2 gap-2 mb-3">
        <button v-for="crop in availableCrops" :key="crop"
                @click="selectedCrop = crop"
                :class="selectedCrop === crop ? 'accent-btn' : 'glass-card'"
                class="p-2 rounded-lg text-sm font-medium transition-all">
          {{ getCropDisplayName(crop) }}
        </button>
      </div>
      
      <div v-if="cropInfo" class="space-y-3">
        <div class="p-3 rounded-lg bg-white/5 dark:bg-black/5">
          <h4 class="font-semibold mb-2">{{ cropInfo.name }}</h4>
          <div class="space-y-2 text-sm">
            <div>
              <span class="font-medium">Seasons:</span>
              <p class="themed-text-secondary">{{ cropInfo.seasons?.join(', ') }}</p>
            </div>
            <div>
              <span class="font-medium">Growing Period:</span>
              <p class="themed-text-secondary">{{ cropInfo.growingPeriod }} days</p>
            </div>
            <div>
              <span class="font-medium">Water Requirement:</span>
              <p class="themed-text-secondary">{{ cropInfo.waterRequirement }}</p>
            </div>
            <div v-if="cropInfo.marketPrice">
              <span class="font-medium">Market Price:</span>
              <p class="themed-text-secondary">
                {{ cropInfo.marketPrice.current }} {{ cropInfo.marketPrice.unit }}
                ({{ cropInfo.marketPrice.trend }})
              </p>
            </div>
          </div>
        </div>
        
        <div v-if="cropInfo.bestPractices" class="p-3 rounded-lg bg-white/5 dark:bg-black/5">
          <h5 class="font-semibold mb-2">Best Practices</h5>
          <ul class="text-sm themed-text-secondary list-disc list-inside">
            <li v-for="practice in cropInfo.bestPractices" :key="practice">{{ practice }}</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Government Policies -->
    <div class="glass-card p-4 mb-4">
      <h3 class="font-bold mb-3">Government Policies & Subsidies</h3>
      <div v-if="policies.length === 0" class="skeleton h-32 rounded"></div>
      <div v-else class="space-y-3">
        <div v-for="policy in policies" :key="policy.id" 
             class="p-3 rounded-lg border-l-4 border-accent">
          <div class="flex justify-between items-start mb-2">
            <h4 class="font-semibold">{{ policy.title }}</h4>
            <span :class="policy.status === 'active' ? 'text-green-600' : 'text-gray-500'" 
                  class="text-xs px-2 py-1 rounded">
              {{ policy.status }}
            </span>
          </div>
          <p class="text-sm themed-text-secondary mb-2">{{ policy.description }}</p>
          <div class="text-xs themed-text-secondary">
            <p>📅 Deadline: {{ formatDate(policy.deadline) }}</p>
            <p>📋 {{ policy.eligibility?.length }} eligibility criteria</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Pest Alerts -->
    <div class="glass-card p-4">
      <h3 class="font-bold mb-3">Pest & Disease Alerts</h3>
      <div v-if="pestAlerts.length === 0" class="text-center py-4 themed-text-secondary">
        No active pest alerts for your region
      </div>
      <div v-else class="space-y-3">
        <div v-for="pest in pestAlerts" :key="pest.id" 
             class="p-3 rounded-lg border-l-4"
             :class="pestSeverityClass(pest.severity)">
          <div class="flex justify-between items-start mb-2">
            <h4 class="font-semibold">{{ pest.pest }}</h4>
            <span :class="pestSeverityClass(pest.severity)" class="text-xs px-2 py-1 rounded">
              {{ pest.severity }}
            </span>
          </div>
          <p class="text-sm themed-text-secondary mb-2">
            🌱 {{ pest.crop }} | 📍 {{ pest.affectedArea }}
          </p>
          <div v-if="pest.symptoms" class="mb-2">
            <p class="text-xs font-semibold mb-1">Symptoms:</p>
            <ul class="text-xs themed-text-secondary list-disc list-inside">
              <li v-for="symptom in pest.symptoms" :key="symptom">{{ symptom }}</li>
            </ul>
          </div>
          <div v-if="pest.treatment">
            <p class="text-xs font-semibold mb-1">Treatment:</p>
            <ul class="text-xs themed-text-secondary list-disc list-inside">
              <li v-for="treatment in pest.treatment" :key="treatment">{{ treatment }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import ThemeToggle from '@/components/ThemeToggle.vue'
import researchService from '@/services/researchService'

const selectedRegion = ref('central')
const selectedCrop = ref('rice')
const weatherAlerts = ref([])
const marketPrices = ref({})
const cropInfo = ref(null)
const policies = ref([])
const pestAlerts = ref([])

const availableCrops = ['rice', 'cassava', 'rubber', 'sugarcane']

const getCropDisplayName = (crop) => {
  const names = {
    rice: 'ข้าว',
    cassava: 'มันสำปะหลัง',
    rubber: 'ยางพารา',
    sugarcane: 'อ้อย'
  }
  return names[crop] || crop
}

const alertSeverityClass = (severity) => {
  const classes = {
    high: 'border-red-500 bg-red-50 dark:bg-red-900/20',
    medium: 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
    low: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
  }
  return classes[severity] || classes.low
}

const alertTypeClass = (type) => {
  const classes = {
    warning: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    info: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    advisory: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
  }
  return classes[type] || classes.info
}

const pestSeverityClass = (severity) => {
  const classes = {
    high: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    low: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
  }
  return classes[severity] || classes.low
}

const formatDateTime = (isoString) => {
  return new Date(isoString).toLocaleString('th-TH', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('th-TH', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

const loadData = async () => {
  try {
    // Load weather alerts
    weatherAlerts.value = await researchService.getWeatherAlerts(selectedRegion.value)
    
    // Load market prices
    marketPrices.value = await researchService.getMarketPrices(availableCrops)
    
    // Load crop info
    cropInfo.value = await researchService.getCropInfo(selectedCrop.value, selectedRegion.value)
    
    // Load government policies
    policies.value = await researchService.getGovernmentPolicies()
    
    // Load pest alerts
    pestAlerts.value = await researchService.getPestAlerts(selectedRegion.value, selectedCrop.value)
  } catch (error) {
    console.error('Failed to load research data:', error)
  }
}

// Watch for crop selection changes
watch(selectedCrop, () => {
  loadData()
})

onMounted(() => {
  loadData()
})
</script>
